import type { CUserAccountsTranslations } from "./pl";

export const de: CUserAccountsTranslations = {
  empty: "Keine Daten zum Anzeigen.",
  registerDisabled: "Registrierung ist deaktiviert.",

  logout: {
    failed: "Abmeldung fehlgeschlagen.",
  },

  actions: {
    login: "Anmelden",
    register: "Konto erstellen",
    remindPass: "Passwort vergessen?",
    backToLogin: "Zurück zur Anmeldung",
    logout: "Abmelden",
  },

  labels: {
    login: "Login oder E-Mail",
    pass: "Passwort",
    rememberMe: "Für 7 Tage angemeldet bleiben",
    email: "E-Mail",
    userGroup: "Benutzergruppe",
    name: "Vorname",
    surname: "Nachname",
    telephone: "Telefon",
    passwordRegister: "Passwort",
    ppass: "Passwort wiederholen",
    regulamin: "Ich akzeptiere die AGB",
    regulaminLink: "ansehen",
    personalData:
      "Ich willige in die Verarbeitung meiner personenbezogenen Daten ein.",
    marketing: "Einwilligung in Marketingkommunikation",
    emailOrLogin: "E-Mail oder Login",
  },

  validation: {
    loginRequired: "Login oder E-Mail-Adresse eingeben.",
    passwordRequired: "Passwort eingeben.",
    emailRequired: "E-Mail-Feld ist erforderlich.",
    invalidEmail: "Ungültige E-Mail-Adresse.",
    passwordRegRequired: "Passwort ist erforderlich.",
    passwordRepeatRequired: "Passwort wiederholen.",
    passwordMismatch: "Passwörter stimmen nicht überein.",
    passwordRequirements:
      "Passwort erfüllt die Anforderungen nicht — siehe Liste unter dem Feld.",
    requiredRegulamin: "Die Annahme der AGB ist erforderlich.",
    requiredPersonalData:
      "Die Einwilligung zur Verarbeitung personenbezogener Daten ist erforderlich.",
    emailOrLoginRequired: "E-Mail oder Login eingeben.",
    newPassRequired: "Neues Passwort eingeben.",
    newPpassRequired: "Neues Passwort wiederholen.",
  },

  passwordStrength: {
    strengthLabel: "Passwortstärke",
    levels: {
      veryWeak: "Sehr schwach",
      weak: "Schwach",
      fair: "Mittel",
      good: "Stark",
    },
    requirements: {
      length: "{min}–{max} Zeichen",
      lowercase: "Kleinbuchstabe (a–z)",
      uppercase: "Großbuchstabe (A–Z)",
      digit: "Ziffer (0–9)",
      special: "Sonderzeichen (!@#$…)",
    },
  },

  login: {
    failed: "Anmeldung fehlgeschlagen.",
  },

  register: {
    failed: "Registrierung fehlgeschlagen.",
    successDefault:
      "Konto erstellt. Prüfe deine E-Mail, um es zu aktivieren.",
    successWithEmail:
      "Registrierung erfolgreich! Ein Aktivierungslink wurde an {email} gesendet. Bitte prüfe deinen Posteingang UND deinen SPAM-/Junk-Ordner — die Nachricht kann dort statt im Hauptpostfach landen.",
    successMailFailed:
      "Dein Konto wurde erstellt, aber wir konnten die Aktivierungs-E-Mail nicht an {email} senden. Bitte kontaktiere den Administrator oder fordere nach dem Einloggen einen neuen Bestätigungslink an.",
    recaptchaHint:
      "Backend erfordert reCAPTCHA — die reCAPTCHA-Komponente für Imperial CMS wird in der nächsten Iteration hinzugefügt. Vorerst ist die Backend-Validierung tolerant (Testmodus).",
  },

  remind: {
    title: "Passwort vergessen",
    hint: "Gib deine E-Mail oder dein Login ein. Wir senden dir einen Reset-Link.",
    submit: "Link senden",
    failed: "Reset-Link konnte nicht gesendet werden.",
    successDefault:
      "Wenn das Konto existiert, wurde ein Passwort-Reset-Link an die E-Mail gesendet.",
  },

  reset: {
    title: "Neues Passwort festlegen",
    newPass: "Neues Passwort",
    repeatPass: "Neues Passwort wiederholen",
    submit: "Passwort ändern",
    invalidLink:
      "Ungültiger Passwort-Reset-Link. Versuche, einen neuen Link zu senden.",
    failed: "Passwort konnte nicht geändert werden.",
    successDefault:
      "Passwort wurde geändert. Du kannst dich jetzt anmelden.",
  },

  profile: {
    loading: "Profildaten werden geladen…",
    loadFailed: "Profildaten konnten nicht geladen werden.",
    expandMenu: "Menü aufklappen",
    collapseMenu: "Menü zuklappen",
    tabs: {
      personal: "Deine Daten",
      password: "Passwort ändern",
      account: "Konto",
      users: "Benutzer",
      security: "Sicherheit",
    },
  },

  security: {
    title: "Zwei-Faktor-Authentifizierung",
    loading: "Sicherheitseinstellungen werden geladen…",
    loadFailed: "Sicherheitseinstellungen konnten nicht geladen werden.",

    setupIntro: "Die Zwei-Faktor-Authentifizierung (2FA) bietet zusätzlichen Schutz für dein Konto. Beim Anmelden gibst du zusätzlich zum Passwort einen 6-stelligen Code aus einer App auf deinem Handy ein.",
    actionSetupStart: "Schlüssel erzeugen",
    setupFailed: "Einrichtung konnte nicht gestartet werden. Bitte erneut versuchen.",

    step1Title: "QR-Code scannen",
    step1Description: "Öffne deine Authenticator-App (Google Authenticator, Microsoft Authenticator) und scanne den QR-Code unten.",
    step2Title: "Code aus der App eingeben",
    step2Description: "Die App erzeugt einen 6-stelligen Code, der alle 30 Sekunden wechselt. Gib ihn unten ein.",

    qrAlt: "QR-Code für Authenticator-App",
    qrManualEntry: "Kannst du nicht scannen? Gib diesen Schlüssel manuell ein:",

    actionEnable: "Authentifizierung aktivieren",
    enableFailed: "Authentifizierung konnte nicht aktiviert werden. Bitte erneut versuchen.",
    invalidCode: "Ungültiger Code. Prüfe den Code in der App und versuche es erneut.",
    rateLimitExceeded: "Zu viele fehlgeschlagene Versuche. Starte die Einrichtung erneut, um einen neuen Schlüssel zu erzeugen.",

    enabledTitle: "Zwei-Faktor-Authentifizierung ist aktiv",
    enabledDescription: "Dein Konto ist durch eine zusätzliche Sicherheitsschicht geschützt. Beim Anmelden ist ein Code aus deiner Authenticator-App erforderlich.",
    disablePasswordLabel: "Aktuelles Passwort",
    actionDisable: "Authentifizierung deaktivieren",
    disableFailed: "Authentifizierung konnte nicht deaktiviert werden.",
    invalidPassword: "Ungültiges Passwort. Prüfe die Schreibweise und versuche es erneut.",
    disableSuccess: "Zwei-Faktor-Authentifizierung wurde deaktiviert.",
    requiredByGroupConfigured: "Deine Benutzergruppe verlangt aktive Zwei-Faktor-Authentifizierung - Deaktivieren ist nicht erlaubt.",
  },

  mfaGuard: {
    title: "Zwei-Faktor-Authentifizierung erforderlich",
    description: "Deine Benutzergruppe verlangt aktive Zwei-Faktor-Authentifizierung. Richte MFA im Tab Sicherheit ein, um dein Konto besser zu schützen.",
    linkToSecurity: "Zur Einrichtung",
  },

  authMfaModal: {
    title: "Zwei-Faktor-Authentifizierung",
    intro: "Gib den 6-stelligen Code aus deiner Authenticator-App ein (Google Authenticator, Microsoft Authenticator).",
    close: "Schliessen",
    verify: "Bestaetigen",
    cancel: "Abbrechen",
    invalidCode: "Ungueltiger Code. Bitte erneut versuchen.",
    sessionExpired: "Verifizierungssitzung abgelaufen.",
    ariaLabel: "Zwei-Faktor-Authentifizierung",
  },

  userList: {
    title: "Benutzer",
    loading: "Benutzerliste wird geladen…",
    loadFailed: "Die Benutzerliste konnte nicht geladen werden.",
    empty: "Keine Benutzer zum Anzeigen.",
    searchPlaceholder: "Suchen...",
    columnUser: "Benutzer",
    columnEmail: "E-Mail",
    columnPhone: "Telefon",
    columnAction: "Aktion",
    sendMessage: "Nachricht senden",
    messagesUnavailable: "nicht verfügbar",
    noEmail: "keine",
    noPhone: "keine",
    truncatedHint: "Die Liste wurde auf die ersten Einträge beschränkt.",
    paginationPrev: "Zurück",
    paginationNext: "Weiter",
    paginationInfo: "Seite {page} von {pages}",
  },
};
